import { useMemo, useState } from "react";

import { ArrowTopRightOnSquareIcon, EyeIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import PartImage from "../../stock/PartImage";
import PriceFormat from "../../../utils/priceFormat";
import classNames from "../../../utils/classNames";
import openInNewTab from "../../../utils/openInNewTab";
import {
  EnrichedListing,
  formatDaysListed,
  listingAgeTone,
} from "./marketplaceInsightsHelpers";

interface Props {
  listings: EnrichedListing[];
  isLoading: boolean;
  limit?: number;
}

/**
 * Top performers — listings ranked highest-to-lowest by estimated views
 * in the last 30 days. Designed as an at-a-glance scan: the first few
 * rows get a subtle highlight to reinforce "these are your winners".
 */
export default function TopViewedListingsTable({
  listings,
  isLoading,
  limit = 15,
}: Props) {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  const sorted = useMemo(
    () => [...listings].sort((a, b) => b.views30d - a.views30d),
    [listings]
  );
  const visible = showAll ? sorted : sorted.slice(0, limit);

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <EyeIcon className="h-4 w-4 text-purple-600" />
            Most viewed listings · last 30 days
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Your strongest listings by eBay / Shopify engagement.
          </p>
        </div>
        {sorted.length > limit && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            {showAll ? "Show top 15" : `Show all ${sorted.length}`}
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <th className="w-8 px-3 py-2">#</th>
              <th className="px-3 py-2">Part</th>
              <th className="px-3 py-2 text-right">Views 30d</th>
              <th className="px-3 py-2 text-right">Watchers</th>
              <th className="px-3 py-2 text-right">Price</th>
              <th className="px-3 py-2 text-right">Age</th>
              <th className="px-3 py-2 text-right">Listed</th>
              <th className="w-10 px-2 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && visible.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-500">
                  Loading marketplace performance…
                </td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-500">
                  No live listings to analyse yet.
                </td>
              </tr>
            ) : (
              visible.map((row, i) => {
                const { part } = row;
                const title = part.title?.trim() || part.category?.name || "Untitled listing";
                const vehicle = part.vehicle
                  ? [part.vehicle.dvlaMake, part.vehicle.dvlaModel]
                      .filter(Boolean)
                      .join(" ")
                  : null;
                const topThree = i < 3 && !showAll;
                return (
                  <tr
                    key={part.id ?? i}
                    onClick={() => navigate(`/ebay-listings/${part.id}`)}
                    className={classNames(
                      "cursor-pointer text-sm transition-colors hover:bg-slate-50",
                      topThree && "bg-gradient-to-r from-purple-50/60 via-white to-white"
                    )}
                  >
                    <td className="px-3 py-2">
                      <span
                        className={classNames(
                          "inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ring-1 ring-inset",
                          i === 0
                            ? "bg-amber-50 text-amber-700 ring-amber-200"
                            : i === 1
                              ? "bg-slate-100 text-slate-700 ring-slate-300"
                              : i === 2
                                ? "bg-orange-50 text-orange-700 ring-orange-200"
                                : "bg-white text-slate-500 ring-slate-200"
                        )}
                      >
                        {i + 1}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 flex-none overflow-hidden rounded-md ring-1 ring-slate-200">
                          <PartImage part={part} width="w-10" />
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-900" title={title}>
                            {title}
                          </div>
                          <div className="truncate text-xs text-slate-500">
                            {part.ebayItemNumber ? (
                              <span className="font-mono">{part.ebayItemNumber}</span>
                            ) : (
                              <span>{part.partNumber ?? "—"}</span>
                            )}
                            {vehicle && <span> · {vehicle}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="text-sm font-semibold tabular-nums text-purple-700">
                        {row.views30d.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                      {row.watchers.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900">
                      {PriceFormat(row.price)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span
                        className={classNames(
                          "inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ring-1 ring-inset",
                          listingAgeTone(row.daysListed)
                        )}
                      >
                        {formatDaysListed(row.daysListed)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-xs tabular-nums text-slate-500">
                      {row.listedOn?.isValid
                        ? row.listedOn.toLocaleString(DateTime.DATE_MED)
                        : "—"}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {part.ebayItemNumber ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openInNewTab(
                              `https://www.ebay.co.uk/itm/${part.ebayItemNumber}`
                            );
                          }}
                          title="Open on eBay"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
